import { ButtonProps, LinkButtonSettings } from '@almbrand/button';
import { CtaGroup } from '@almbrand/ctagroup';
import { RichText } from '@almbrand/richtext';
import { pushToDataLayer } from '@almbrand/trackinghooks';
import classNames from 'classnames';
import { ctaGroupMargins } from 'common/buttonUtils';
import { useEffect, useRef, useState } from 'react';

import './ParagraphComponent.scss';

export interface ParagraphComponentProps extends LocalId {
	//	countdownPositionTop: boolean;
	//	header: string;
	contentType?: string;
	richText: string;
	contentText?: string; // hidden text
	padding?: string;
	backgroundColor?: BackgroundColor; // TODO: map in component library
	buttons?: Button[];
	ctaMarginTop?: string;
	ctaMarginBottom?: string;
	contentId?: number | string;
	trackAsPromotion: boolean;
}

export const ParagraphComponent: React.FC<ParagraphComponentProps> = ({
	contentId,
	contentType,
	backgroundColor,
	padding,
	richText,
	contentText,
	buttons,
	ctaMarginTop,
	ctaMarginBottom,
	useReadline,
	readlineId,
	trackAsPromotion,
}) => {
	const [isContentTextVisible, setIsContentTextVisible] = useState(false);
	const contentRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (contentRef.current) {
			if (isContentTextVisible) {
				contentRef.current.style.maxHeight = `${contentRef.current.scrollHeight}px`;
			} else {
				contentRef.current.style.maxHeight = '0';
			}
		}

		setTimeout(() => {
			handleViewPromotionTracking();
		}, 1250);
	}, [isContentTextVisible]);

	const toggleContentTextVisibility = () => {
		setIsContentTextVisible(!isContentTextVisible);
	};

	const mapButtonsToCtaGroup = (buttons: Button[]): ButtonProps[] =>
		buttons.map(({ title = '', type, color, link, target }) => ({
			children: title,
			buttonType: (type === 'cta' ? color : type) as ButtonProps['buttonType'],
			linkButtonSettings: {
				title,
				href: link?.url,
				target: target as LinkButtonSettings['target'],
			},
			trackingEvent: {
				event: 'trackNavigation',
				navigationAction: title ?? '',
				navigationDestination: link?.url ?? '',
				navigationType: 'call-to-action',
			},
			...(trackAsPromotion && {
				customTrackingEvent: {
					event: 'select_promotion',
					ecommerce: {
						items: [
							{
								creative_name: 'paragraph promotion',
								creative_slot: contentType,
								promotion_name: handleGtmHeader(richText, 'missing promotion name'),
							},
						],
					},
				},
			}),
		}));

	const handleGtmHeader = (richText: string, alternativeGtmHeader: string) => {
		const parser = new DOMParser();
		const parsedDocument = parser.parseFromString(richText, 'text/html');
		const h2Element = parsedDocument.querySelector('h2');
		const gtmRichTextHeader = h2Element ? h2Element.textContent : alternativeGtmHeader;

		return gtmRichTextHeader;
	};

	const handleViewPromotionTracking = () => {
		if (!trackAsPromotion || !richText) return;
		console.log('view promotion');

		pushToDataLayer({ ecommerce: null });
		pushToDataLayer({
			event: 'view_promotion',
			ecommerce: {
				items: [
					{
						creative_name: 'paragraph promotion',
						creative_slot: contentType,
						promotion_name: handleGtmHeader(richText, 'missing promotion name'),
					},
				],
			},
		});
	};

	return (
		<div
			className={classNames('paragraph', backgroundColor?.cssClass, `p-${padding}`)}
			style={
				{
					'--blockBgColor':
						!isContentTextVisible && contentText
							? backgroundColor?.colorHex === '0'
								? '#fff'
								: backgroundColor?.colorHex
							: '',
				} as React.CSSProperties
			}
		>
			<div className={!isContentTextVisible && contentText && 'paragraph__add-fade'}>
				<RichText
					id={useReadline && readlineId ? readlineId : contentId}
					description={richText}
				/>
			</div>
			{buttons && (
				<CtaGroup
					ctaGroup={mapButtonsToCtaGroup(buttons)}
					className={ctaGroupMargins(ctaMarginTop, ctaMarginBottom)}
				/>
			)}
			{/* Hidden text */}
			{isContentTextVisible && contentText && (
				<div
					className='paragraph__contentText'
					ref={contentRef}
					style={{
						maxHeight: isContentTextVisible ? `${contentRef.current?.scrollHeight}px` : '0',
					}}
				>
					<RichText description={contentText} />
				</div>
			)}

			{contentText && (
				<div
					className={classNames(
						'paragraph__contentVisible--triggerWrapper',
						isContentTextVisible && 'paragraph__contentVisible--active'
					)}
				>
					<hr />
					<button onClick={toggleContentTextVisibility}>
						<div className='paragraph__contentVisible--triggerIcon'>
							<span></span>
							<span></span>
						</div>
					</button>
					<hr />
				</div>
			)}
		</div>
	);
};
